/**
 * PromptDialog.jsx
 * ─────────────────
 * Styled in-app prompt modal — replaces native `window.prompt()` which
 * can't be styled, can't render hints/validation, and is blocked by
 * Playwright's default browser context.
 *
 * Dismisses via Cancel button, Close (X) button, outside-overlay click,
 * or the Escape key (handled by the parent provider). Submits via the
 * primary button or the Enter key inside the input.
 *
 * Test selectors are derived from the `testid` prop:
 *   <PromptDialog testid="my-prompt" />  →
 *     [data-testid="my-prompt"]              (modal root)
 *     [data-testid="my-prompt-close"]        (X button)
 *     [data-testid="my-prompt-cancel"]       (Cancel button)
 *     [data-testid="my-prompt-input"]        (text input)
 *     [data-testid="my-prompt-submit"]       (primary action)
 */
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

export default function PromptDialog({
  title,
  body,
  defaultValue = "",
  placeholder = "",
  submitLabel = "Save",
  busy = false,
  required = true,
  onSubmit,
  onCancel,
  testid = "prompt-dialog",
}) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    // Autofocus + select-all so the user can type-to-replace.
    const t = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 30);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const trimmed = (value || "").trim();
    if (required && !trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <div
      data-testid={testid}
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.5)", zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          background: "var(--surface)", borderRadius: 12,
          width: 480, maxHeight: "85vh", overflow: "auto",
          padding: "22px 24px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            data-testid={`${testid}-close`}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, lineHeight: 0 }}
          >
            <X style={{ width: 14, height: 14, color: "var(--text-tertiary)" }} />
          </button>
        </div>
        {body && (
          <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.55, marginBottom: 12 }}>
            {body}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          disabled={busy}
          data-testid={`${testid}-input`}
          style={{
            width: "100%", boxSizing: "border-box",
            border: "1px solid var(--border)", borderRadius: 8,
            padding: "8px 10px", fontSize: 13, fontFamily: "inherit",
            background: "var(--bg)", color: "var(--text-primary)",
            marginBottom: 18,
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testid}-cancel`}
            style={{ fontSize: 12, padding: "7px 14px" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || (required && !(value || "").trim())}
            data-testid={`${testid}-submit`}
            style={{ fontSize: 12, padding: "7px 14px", opacity: busy ? 0.55 : 1 }}
          >
            {busy ? "Working…" : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
